import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { IoClose } from 'react-icons/io5';
import { editGenUploadMetadata, getDocumentById } from '../../api/api';
import { notifyError } from '../../errors/notifyError';
import { OP } from '../../api/operations';
import '../UploadModal/UploadModal.css';

Modal.setAppElement('#root');

const Req = () => <span className="req" aria-hidden="true">*</span>;

const intervalToLabel = (interval) => {
  if (interval === 15 || interval === '15') return '15 mins';
  if (interval === 60 || interval === '60') return '1 hr';
  if (interval === '15 mins' || interval === '1 hr') return interval;
  return '15 mins';
};

/**
 * Metadata-only editor for an uploaded generation profile.
 * Opened from configured wind/solar generation nodes.
 */
const EditGenerationProfileModal = ({ isOpen, onRequestClose, profile, onSaved }) => {
  const [type, setType] = useState('solar');
  const [makeAndModel, setMakeAndModel] = useState('');
  const [location, setLocation] = useState('');
  const [subLocation, setSubLocation] = useState('');
  const [poe, setPoe] = useState('');
  const [maxRange, setMaxRange] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [interval, setInterval] = useState('15 mins');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [loadedProfile, setLoadedProfile] = useState(null);

  const objectId = profile?._id || profile?.id || loadedProfile?._id;
  const effectiveProfile = loadedProfile || profile;
  const isLegacyMissingMeta = !effectiveProfile?.sub_location && !effectiveProfile?.poe;

  const applyProfile = (doc) => {
    if (!doc) return;
    setType(doc.type || 'solar');
    setMakeAndModel(doc.makeAndModel || '');
    setLocation(doc.location || '');
    setSubLocation(doc.sub_location || '');
    setPoe(doc.poe || '');
    setMaxRange(doc.maxRange || '');
    setStartDate(doc.start_Date || doc.startDate || '');
    setEndDate(doc.end_Date || doc.endDate || '');
    setInterval(intervalToLabel(doc.interval));
  };

  useEffect(() => {
    if (!isOpen) {
      setLoadedProfile(null);
      return;
    }

    const hydrate = async () => {
      setErrors({});
      setSubmitError(null);

      if (profile?.makeAndModel) {
        setLoadedProfile(profile);
        applyProfile(profile);
        return;
      }

      const id = profile?._id || profile?.id;
      if (!id) return;

      setIsLoading(true);
      try {
        const documentData = await getDocumentById(id);
        setLoadedProfile(documentData);
        applyProfile(documentData);
      } catch (error) {
        const info = notifyError(error, {
          operation: OP.genUpload.getDocumentById,
          showToast: false,
        });
        setSubmitError(info);
      } finally {
        setIsLoading(false);
      }
    };

    hydrate();
  }, [isOpen, profile]);

  const validateForm = () => {
    const newErrors = {};
    if (!type.trim()) newErrors.type = 'Type is required';
    if (!makeAndModel.trim()) newErrors.makeAndModel = 'Make and model is required';
    if (!location.trim()) newErrors.location = 'Location is required';
    else if (location.trim().startsWith('Test_')) {
      newErrors.location =
        "Location must not start with 'Test_'. Profile type is set via Test / Actual on upload.";
    }
    if (!isLegacyMissingMeta) {
      if (!subLocation.trim()) newErrors.subLocation = 'Sub-location is required';
      if (!poe.trim()) newErrors.poe = 'PoE is required';
    }
    if (!maxRange.trim()) newErrors.maxRange = 'Max range is required';
    if (!startDate.trim()) newErrors.startDate = 'Start date is required';
    if (!endDate.trim()) newErrors.endDate = 'End date is required';
    if (!interval.trim()) newErrors.interval = 'Interval is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!objectId) {
      setSubmitError({ title: 'Missing profile', message: 'Could not resolve profile id.' });
      return;
    }
    if (!validateForm()) return;

    setIsSaving(true);
    setSubmitError(null);
    try {
      const response = await editGenUploadMetadata(objectId, {
        type,
        makeAndModel,
        location,
        maxRange,
        startDate,
        endDate,
        interval,
        subLocation: subLocation.trim(),
        poe: poe.trim(),
      });
      const updated = response?.document || {
        ...effectiveProfile,
        type,
        makeAndModel,
        location,
        maxRange,
        start_Date: startDate,
        end_Date: endDate,
        interval: interval === '1 hr' ? 60 : 15,
        sub_location: subLocation.trim() || undefined,
        poe: poe.trim() || undefined,
        _id: objectId,
      };
      onSaved?.(updated);
      onRequestClose();
    } catch (error) {
      const info = notifyError(error, {
        operation: OP.genUpload.editGenUploadMetadata,
        showToast: false,
      });
      setSubmitError(info);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Edit Generation Profile"
      className="modal upload-modal"
      overlayClassName="overlay"
    >
      <div className="modal-header">
        <div>
          <h2>Edit Generation Profile</h2>
          <p className="modal-header-subtitle">
            Metadata only — the CSV file is unchanged
          </p>
        </div>
        <IoClose className="close-icon" onClick={onRequestClose} aria-label="Close" />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="modal-content">
          {isLoading && <div className="form-loading">Loading profile…</div>}

          {submitError && (
            <div className="form-banner form-banner-alert" role="alert">
              <div>
                <strong>{submitError.title}</strong>
                <div>{submitError.message}</div>
              </div>
            </div>
          )}

          <section className="form-section">
            <h3 className="form-section-title">Profile</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="edit-type">Type <Req /></label>
                <select
                  id="edit-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={errors.type ? 'error' : ''}
                  disabled={isLoading}
                >
                  <option value="solar">Solar</option>
                  <option value="wind">Wind</option>
                </select>
                {errors.type && <div className="error-message">{errors.type}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="edit-location">Location <Req /></label>
                <input
                  type="text"
                  id="edit-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className={errors.location ? 'error' : ''}
                  disabled={isLoading}
                />
                {errors.location && <div className="error-message">{errors.location}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="edit-subLocation">
                  Sub-location
                  {isLegacyMissingMeta ? (
                    <span className="opt">(optional)</span>
                  ) : (
                    <Req />
                  )}
                </label>
                <input
                  type="text"
                  id="edit-subLocation"
                  value={subLocation}
                  onChange={(e) => setSubLocation(e.target.value)}
                  className={errors.subLocation ? 'error' : ''}
                  placeholder="e.g., Site A / Block 1"
                  disabled={isLoading}
                />
                {errors.subLocation && <div className="error-message">{errors.subLocation}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="edit-poe">
                  PoE
                  {isLegacyMissingMeta ? (
                    <span className="opt">(optional)</span>
                  ) : (
                    <Req />
                  )}
                </label>
                <input
                  type="text"
                  id="edit-poe"
                  value={poe}
                  onChange={(e) => setPoe(e.target.value)}
                  className={errors.poe ? 'error' : ''}
                  placeholder="e.g., P90, P75"
                  disabled={isLoading}
                />
                {errors.poe && <div className="error-message">{errors.poe}</div>}
              </div>

              <div className="form-group span-2">
                <label htmlFor="edit-makeAndModel">Make and Model <Req /></label>
                <input
                  type="text"
                  id="edit-makeAndModel"
                  value={makeAndModel}
                  onChange={(e) => setMakeAndModel(e.target.value)}
                  className={errors.makeAndModel ? 'error' : ''}
                  disabled={isLoading}
                />
                {errors.makeAndModel && <div className="error-message">{errors.makeAndModel}</div>}
              </div>
            </div>
          </section>

          <section className="form-section">
            <h3 className="form-section-title">Time range</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="edit-maxRange">Max Range <Req /></label>
                <select
                  id="edit-maxRange"
                  value={maxRange}
                  onChange={(e) => setMaxRange(e.target.value)}
                  className={errors.maxRange ? 'error' : ''}
                  disabled={isLoading}
                >
                  <option value="">Select a range</option>
                  <option value="1yr">1 year</option>
                  <option value="6mo">6 months</option>
                  <option value="3mo">3 months</option>
                  <option value="1mo">1 month</option>
                </select>
                {errors.maxRange && <div className="error-message">{errors.maxRange}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="edit-interval">Interval <Req /></label>
                <select
                  id="edit-interval"
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                  className={errors.interval ? 'error' : ''}
                  disabled={isLoading}
                >
                  <option value="15 mins">15 mins</option>
                  <option value="1 hr">1 hr</option>
                </select>
                {errors.interval && <div className="error-message">{errors.interval}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="edit-startDate">Start Date <Req /></label>
                <input
                  type="text"
                  id="edit-startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={errors.startDate ? 'error' : ''}
                  placeholder="DD/MM/YYYY"
                  disabled={isLoading}
                />
                {errors.startDate && <div className="error-message">{errors.startDate}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="edit-endDate">End Date <Req /></label>
                <input
                  type="text"
                  id="edit-endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={errors.endDate ? 'error' : ''}
                  placeholder="DD/MM/YYYY"
                  disabled={isLoading}
                />
                {errors.endDate && <div className="error-message">{errors.endDate}</div>}
              </div>
            </div>
          </section>
        </div>

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onRequestClose} disabled={isSaving}>
            Cancel
          </button>
          <button type="submit" className="upload-button" disabled={isSaving || isLoading || !objectId}>
            {isSaving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditGenerationProfileModal;
